import React from 'react';

const FixedLocationSelector = ({ 
  fixedLocationData, 
  selectedLocation, 
  onLocationChange, 
  primaryColor = 'blue-600',
  themeMode = 'light',
  businessName 
}) => {
  const { locations = [] } = fixedLocationData || {};
  // NOTE: single-location auto-select is owned by useAppointmentSetup (which calls
  // the raw location setter, so it never flags user intent / never scrolls on
  // arrival). This component intentionally does NOT auto-select — only the
  // multi-location radio tap below calls onLocationChange (the flagging wrapper).

  // Don't show anything if no locations available
  if (!locations || locations.length === 0) {
    return null;
  }

  const getRadioStyles = (isSelected) => {
    if (isSelected) return 'radio-themed-selected';
    return 'border-themed';
  };

  // If only one location, show informational display instead of radio buttons
  if (locations.length === 1) {
    const singleLocation = locations[0];
    const isAddressHidden = !singleLocation.address && !singleLocation.full;
    return (
      <div className="space-y-4">
        <div>
          <h3 className={`text-lg font-semibold text-themed-primary mb-2`}>
            Appointment Location
          </h3>
          <p className={`text-sm text-themed-secondary`}>
            Your appointment will be at:
          </p>
        </div>

        <div className="rounded-lg border border-themed bg-themed-card-elevated p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3 leading-none">
              📍
            </span>
            <span className="block text-sm font-medium text-themed-primary">
              {singleLocation.name}
            </span>
          </div>
          {isAddressHidden ? (
            <p className="mt-2 text-sm text-themed-secondary">
              Exact address shared once your booking is confirmed.
            </p>
          ) : (
            <p className="mt-1 text-sm text-themed-secondary">
              {singleLocation.address || singleLocation.full}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Multiple locations - show radio button interface
  return (
    <div className="space-y-4">
      <div>
        <h3 className={`text-lg font-semibold text-themed-primary mb-2`}>
          Choose Your Location
        </h3>
        <p className={`text-sm text-themed-secondary`}>
          Select where you'd like your appointment at {businessName || 'our location'}
        </p>
      </div>

      <div className="space-y-3">
        {locations.map((location) => {
          const isAddressHidden = !location.address && !location.full;
          return (
            <label
              key={location.id}
              className="relative flex cursor-pointer rounded-lg border border-themed bg-themed-card p-4 shadow-sm focus:outline-none hover-themed-tint transition-colors duration-200"
            >
              <input
                type="radio"
                name="fixedLocation"
                value={location.id}
                checked={selectedLocation?.id === location.id}
                onChange={() => onLocationChange(location)}
                className="sr-only"
              />
              <span
                className={`flex size-4 items-center justify-center rounded-full border mt-0.5 ${
                  getRadioStyles(selectedLocation?.id === location.id)
                }`}
              >
                {selectedLocation?.id === location.id && (
                  <span className="h-2 w-2 rounded-full bg-white" />
                )}
              </span>
              <span className="ml-3 flex flex-col">
                <span className={`block text-sm font-medium text-themed-primary`}>
                  📍 {location.name}
                </span>
                {!isAddressHidden && (
                  <span className="text-sm text-themed-secondary">
                    {location.address || location.full}
                  </span>
                )}
                {isAddressHidden && (
                  <span className="text-sm text-themed-secondary">
                    Exact address shared once your booking is confirmed.
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default FixedLocationSelector;